import { Button, EmptyState, useToast } from "@hightouch/htui";
import slugify from "@sindresorhus/slugify";
import { nanoid } from "nanoid";
import { Navigate, RouteObject, useNavigate } from "src/router";
import {
  CreateDestinationV2MutationVariables,
  useCreateDestinationV2Mutation,
} from "src/graphql";
import { useQueryString } from "src/utils/use-query-string";
import { useFlags } from "launchdarkly-react-client-sdk";
import { lazyNamed } from "src/utils/lazy-named";

const TestErrorBoundary = lazyNamed(
  () => import("src/pages/test-error-boundary"),
  "TestErrorBoundary",
);

const CreateDestination = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryState = useQueryString();

  const input: {
    name: string;
    type: string;
    config: Record<string, any>;
  } = queryState.data.input ? JSON.parse(atob(queryState.data.input)) : {};

  const { mutateAsync: createDestination, isLoading } =
    useCreateDestinationV2Mutation({
      onSuccess: (data) => {
        toast({
          id: "destination-created",
          title:
            "Destination created successfully. You will be redirected shortly.",
          variant: "success",
        });
        navigate(`/destinations/${data.createDestinationWithSecrets.id}`);
      },
      onError: () => {
        toast({
          id: "destination-error",
          title: "Could not create destination. Please refresh.",
          variant: "error",
        });
      },
    });

  return (
    <EmptyState
      m={8}
      title="Create a demo destination"
      message="Create a destination to get started"
      actions={
        <Button
          onClick={async () => {
            const destination: CreateDestinationV2MutationVariables["destination"] =
              {
                ...input,
                slug: slugify(input.name) + "-" + nanoid(),
                setup_complete: true,
              };

            await createDestination({ destination });
          }}
          isLoading={isLoading}
        >
          Add a destination
        </Button>
      }
    />
  );
};

// Wrapper to handle feature flag for error test page
const MiscRoutesWrapper = () => {
  const { appErrorTestPage } = useFlags();

  if (!appErrorTestPage) {
    return <Navigate to="/" replace />;
  }

  return <TestErrorBoundary />;
};

export const routes: Array<RouteObject> = [
  {
    path: "misc/create-destination",
    element: <CreateDestination />,
    spacesAccess: "hidden-from-limited-view",
  },
  {
    path: "misc/test-error-boundary",
    element: <MiscRoutesWrapper />,
    spacesAccess: "hidden-from-limited-view",
  },
];
